import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { BdsChipClickable, BdsIcon } from '@/core/constants/webComponents';
import { normalizeString as normalize } from '@/core/utils/normalizeString';
import { type SelectionMap, type SelectionState } from '@/core/components/FilterSelector/types';
import { useClickOutside } from '@/core/hooks/useClickOutside';
import { useDebouncedSearch } from '@/core/hooks/useDebouncedSearch';
import { useDropdownAutoScroll } from '@/core/hooks/useDropdownAutoScroll';
import './InterestSearchSelect.scss';

const TRANSITIONS_WITH_NEGATION: Record<string, SelectionState | null> = {
  null: 'add',
  add: 'remove',
  remove: null,
};

const TRANSITIONS_WITHOUT_NEGATION: Record<string, SelectionState | null> = {
  null: 'add',
  add: null,
};

const NegationIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.99967 1.33301C9.78034 1.33301 11.4546 2.02647 12.7138 3.28562C13.9728 4.54478 14.6663 6.21897 14.6663 7.99967C14.6663 9.78034 13.9728 11.4546 12.7138 12.7138C11.4546 13.9728 9.78034 14.6663 7.99967 14.6663C6.21897 14.6663 4.54478 13.9728 3.28562 12.7138C2.02647 11.4546 1.33301 9.78034 1.33301 7.99967C1.33301 6.21897 2.02647 4.54478 3.28562 3.28562C4.54478 2.02647 6.21897 1.33301 7.99967 1.33301ZM7.99967 13.6247C11.1013 13.6247 13.6247 11.1013 13.6247 7.99967C13.6247 4.89807 11.1013 2.37467 7.99967 2.37467C4.89807 2.37467 2.37467 4.89807 2.37467 7.99967C2.37467 11.1013 4.89807 13.6247 7.99967 13.6247ZM5.13509 8.52102H10.8643V7.47935H5.13509V8.52102Z" fill="#E60F0F" />
  </svg>
);

export interface InterestSearchSelectProps {
  label: string;
  placeholder: string;
  loadingText: string;
  emptyText: string;
  searchFn: (term: string) => Promise<string[]>;
  selection: SelectionMap;
  onChange: React.Dispatch<React.SetStateAction<SelectionMap>>;
  allowNegation?: boolean;
}

export const InterestSearchSelect: React.FC<InterestSearchSelectProps> = ({
  label,
  placeholder,
  loadingText,
  emptyText,
  searchFn,
  selection,
  onChange,
  allowNegation = true,
}) => {
  const {
    searchTerm,
    isOpen,
    isLoading,
    searchResults,
    handleSearchTermChange,
    openSearch,
    closeDropdown,
  } = useDebouncedSearch({ searchFn });

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<(HTMLElement | null)[]>([]);

  const selectionEntries = useMemo(() => Object.entries(selection), [selection]);

  const outsideRefs = useMemo(() => [fieldRef, dropdownRef], []);

  useClickOutside(outsideRefs, closeDropdown, isOpen);

  const handleInputClick = openSearch;

  const handleToggleItem = useCallback((item: string) => {
    onChange((prev) => {
      const current = prev[item] ?? null;
      const transitions = allowNegation ? TRANSITIONS_WITH_NEGATION : TRANSITIONS_WITHOUT_NEGATION;
      const next = transitions[String(current)] ?? null;

      const updated = { ...prev };
      delete updated[item];
      return next ? { ...updated, [item]: next } : updated;
    });
  }, [onChange, allowNegation]);

  const handleRemoveItem = useCallback((item: string) => {
    onChange((prev) => {
      const updated = { ...prev };
      delete updated[item];
      return updated;
    });
  }, [onChange]);

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    selectionEntries.forEach(([key], index) => {
      const el = chipRefs.current[index];
      if (!el) return;
      const handler = (e: Event) => { e.stopPropagation(); handleRemoveItem(key); };
      el.addEventListener('chipClickableClose', handler);
      cleanups.push(() => el.removeEventListener('chipClickableClose', handler));
    });
    return () => cleanups.forEach((fn) => fn());
  }, [selectionEntries, handleRemoveItem]);

  const filtered = useMemo(() => {
    const q = normalize(searchTerm.trim());
    return q ? searchResults.filter((item) => normalize(item).includes(q)) : searchResults;
  }, [searchResults, searchTerm]);

  const getOptionState = (key: string): SelectionState | null => selection[key] ?? null;

  const showDropdown = isOpen;
  const hasResults = filtered.length > 0;

  useDropdownAutoScroll(showDropdown, dropdownRef, selectionEntries.length);

  return (
    <div className="interest-select" ref={containerRef}>
      <span className="interest-select__label">{label}</span>
      <div
        ref={fieldRef}
        className={`interest-select__field${showDropdown ? ' interest-select__field--open' : ''}`}
      >
        <div className="interest-select__chips">
          {selectionEntries.map(([key, state], index) => (
            <button
              key={key}
              type="button"
              className="interest-select__chip-wrapper"
              onClick={(e) => e.stopPropagation()}
            >
              <BdsChipClickable
                ref={(el: HTMLElement | null) => { chipRefs.current[index] = el; }}
                color="outline"
                close
              >
                <span className="interest-select__chip-content">
                  {state === 'remove' && <NegationIcon />}
                  <span className="interest-select__chip-text">{key}</span>
                </span>
              </BdsChipClickable>
            </button>
          ))}
          <input
            ref={inputRef}
            className="interest-select__input interest-select__input--highlighted"
            value={searchTerm}
            placeholder={selectionEntries.length === 0 ? placeholder : ''}
            onChange={handleSearchTermChange}
            onClick={handleInputClick}
            onFocus={handleInputClick}
            autoComplete="off"
          />
        </div>

        <button
          type="button"
          className={`interest-select__arrow${showDropdown ? ' interest-select__arrow--open' : ''}`}
          onClick={() => inputRef.current?.focus()}
          tabIndex={-1}
          aria-label="Open dropdown"
        >
          <BdsIcon name="arrow-down" size="x-small" aria-hidden="true" />
        </button>
      </div>

      {showDropdown && (
        <div className="interest-select__dropdown" ref={dropdownRef}>
          <ul className="interest-select__list">
            {isLoading && (
              <li className="interest-select__option interest-select__option--empty">
                <span className="interest-select__option-text">{loadingText}</span>
              </li>
            )}

            {!isLoading && !hasResults && (
              <li className="interest-select__option interest-select__option--empty">
                <span className="interest-select__option-text">{emptyText}</span>
              </li>
            )}

            {!isLoading && hasResults && filtered.map((item) => {
              const state = getOptionState(item);
              const checkboxClass = `interest-select__checkbox interest-select__checkbox--${state ?? 'empty'}`;

              return (
                <li key={item} className="interest-select__option">
                  <button
                    type="button"
                    className="interest-select__option-btn"
                    onClick={(e) => { e.stopPropagation(); handleToggleItem(item); }}
                  >
                    <span className={checkboxClass}>
                      {state === 'add' && <span className="cf-selector__check-icon">✓</span>}
                      {state === 'remove' && <span className="cf-selector__check-icon">−</span>}
                    </span>
                    <span className="interest-select__option-text">{item}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};